/**
 * Shared content for map filters, styles, and lists.
 * This is used by both the persistent MapSidebar (desktop) and the floating MapFiltersPanel (mobile).
 */

import { Separator } from '@/components/ui/separator';
import { MapStyleControl } from './MapStyleControl';
import { MapFilters } from './MapFilters';
import { PlacesList } from './PlacesList';
import { EventsList } from './EventsList';
import { cn } from '@/lib/utils';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';

interface GeographicEntityWithRelevance extends GeographicEntity {
    relevanceType?: string;
    relevanceReason?: string;
}

interface EventMarkerData {
    event: HistoricalEventByPassage | HistoricalEvent;
    position: unknown;
    matchingPlace: GeographicEntity;
}

interface MapContentProps {
    // ... no changes needed if ...props is used or if types are updated elsewhere
}

interface MapContentProps {
    selectedStyleId: string;
    onStyleChange: (styleId: string) => void;
    showLabels: boolean;
    onShowLabelsChange: (show: boolean) => void;
    showAncientRoads: boolean;
    onShowAncientRoadsChange: (show: boolean) => void;
    showSeaLanes: boolean;
    onShowSeaLanesChange: (show: boolean) => void;
    showRomanProvinces: boolean;
    onShowRomanProvincesChange: (show: boolean) => void;
    showAncientEmpires: boolean;
    onShowAncientEmpiresChange: (show: boolean) => void;
    passageEventsOnly: boolean;
    onPassageEventsOnlyChange: (show: boolean) => void;
    availablePlaceTypes: string[];
    selectedPlaceTypes: Set<string>;
    onPlaceTypesChange: (types: Set<string>) => void;
    placeTypeCounts: Map<string, number>;
    availableEventCategories: string[];
    selectedEventCategories: Set<string>;
    onEventCategoriesChange: (categories: Set<string>) => void;
    eventCategoryCounts: Map<string, number>;
    visiblePlaces: GeographicEntityWithRelevance[];
    eventMarkers: EventMarkerData[];
    onPlaceClick: (placeName: string, coordinates: { latitude: number; longitude: number }) => void;
    onEventClick: (eventName: string, coordinates: { latitude: number; longitude: number }) => void;
    passageYear?: number | null;
    className?: string;
}

export function MapContent({
    selectedStyleId,
    onStyleChange,
    showLabels,
    onShowLabelsChange,
    showAncientRoads,
    onShowAncientRoadsChange,
    showSeaLanes,
    onShowSeaLanesChange,
    showRomanProvinces,
    onShowRomanProvincesChange,
    showAncientEmpires,
    onShowAncientEmpiresChange,
    passageEventsOnly,
    onPassageEventsOnlyChange,
    availablePlaceTypes,
    selectedPlaceTypes,
    onPlaceTypesChange,
    placeTypeCounts,
    availableEventCategories,
    selectedEventCategories,
    onEventCategoriesChange,
    eventCategoryCounts,
    visiblePlaces,
    eventMarkers,
    onPlaceClick,
    onEventClick,
    passageYear,
    className
}: MapContentProps) {
    const isDarkMode = useDarkMode();

    return (
        <div className={cn("p-4 space-y-4", className)}>
            {/* Map Style Controls */}
            <div>
                <MapStyleControl
                    selectedStyle={selectedStyleId}
                    onStyleChange={onStyleChange}
                    showLabels={showLabels}
                    onShowLabelsChange={onShowLabelsChange}
                    showAncientRoads={showAncientRoads}
                    onShowAncientRoadsChange={onShowAncientRoadsChange}
                    showSeaLanes={showSeaLanes}
                    onShowSeaLanesChange={onShowSeaLanesChange}
                    showRomanProvinces={showRomanProvinces}
                    onShowRomanProvincesChange={onShowRomanProvincesChange}
                    showAncientEmpires={showAncientEmpires}
                    onShowAncientEmpiresChange={onShowAncientEmpiresChange}
                    passageEventsOnly={passageEventsOnly}
                    onPassageEventsOnlyChange={onPassageEventsOnlyChange}
                />
            </div>

            <Separator className={isDarkMode ? "bg-white/20" : "bg-black/10"} />

            {/* Filters */}
            <div>
                <h4 className="text-xs font-semibold mb-2 uppercase tracking-wide opacity-70">Filters</h4>
                <MapFilters
                    availablePlaceTypes={availablePlaceTypes}
                    selectedPlaceTypes={selectedPlaceTypes}
                    onPlaceTypesChange={onPlaceTypesChange}
                    placeTypeCounts={placeTypeCounts}
                    availableEventCategories={availableEventCategories}
                    selectedEventCategories={selectedEventCategories}
                    onEventCategoriesChange={onEventCategoriesChange}
                    eventCategoryCounts={eventCategoryCounts}
                />
            </div>

            <Separator className={isDarkMode ? "bg-white/20" : "bg-black/10"} />

            {/* Places List */}
            <PlacesList
                places={visiblePlaces}
                onPlaceClick={onPlaceClick}
                passageYear={passageYear}
            />

            {/* Events List */}
            {eventMarkers.length > 0 && (
                <>
                    <Separator className={isDarkMode ? "bg-white/20" : "bg-black/10"} />
                    <EventsList
                        eventMarkers={eventMarkers}
                        onEventClick={onEventClick}
                    />
                </>
            )}
        </div>
    );
}
